/**
 * Progress Data Transformer
 * 
 * Transforms backend progress data format to match frontend UI structure.
 */

// Service name to display name mapping
//
// Engine=v2 stage names emitted by Backend/Services/_common/progress_emit.py:
//   tiling, csf, road_detection, car_removal, file_transfer, create_rsa,
//   ssd_classification, shapefile, automatic_reports.
// Engine=v1 also emits: horizontal, slope_calc, psd_classification,
//   embankment, clear_zone, guardrail, automatic_report, report_generation.
export const SERVICE_DISPLAY_NAMES = {
  'tiling': 'File Tiling',
  'csf': 'Ground Classification',
  'road_detection': 'Road Detection',
  'car_removal': 'Vehicle Removal',
  'file_transfer': 'File Transfer',
  'create_rsa': 'RSA Analysis',
  'ssd_classification': 'SSD Classification',
  'psd_classification': 'PSD Classification',
  'shapefile': 'Shapefile Generation',
  'horizontal': 'Horizontal Curves',
  'slope_calc': 'Slope Calculation',
  'embankment': 'Embankment Analysis',
  'clear_zone': 'Clear Zone Analysis',
  'guardrail': 'Guardrail Analysis',
  'automatic_report': 'Report Generation',
  'automatic_reports': 'Auto Report',
  'report_generation': 'Report Generation'
};

// Service execution order (for step numbering). Mirrors the engine=v2 SF
// chain order (Tiling → CSF → RoadDetection → CarRemoval → FileTransfer →
// CreateRSA → SSDClassification → Shapefile → AutomaticReports), with
// engine=v1-only stages (horizontal, slope_calc, etc.) interleaved where
// they semantically belong.
export const SERVICE_ORDER = [
  'tiling',
  'csf',
  'road_detection',
  'car_removal',
  'file_transfer',
  'create_rsa',
  'ssd_classification',
  'psd_classification',
  'horizontal',
  'slope_calc',
  'embankment',
  'clear_zone',
  'guardrail',
  'shapefile',
  'automatic_reports',
  'automatic_report',
  'report_generation'
];

/**
 * Transform backend progress data to frontend UI format
 * @param {Object} backendData - Progress data from backend
 * @param {Object} taskMetadata - Additional task metadata (project_id, run_id, etc.)
 * @returns {Object} Transformed data matching UI structure
 */
export const transformProgressData = (backendData, taskMetadata = {}) => {
  const { services = {}, overall_progress = 0, completed_services = 0, total_services = 0 } = backendData;
  
  // Get all service names and sort by execution order
  const serviceNames = Object.keys(services).sort((a, b) => {
    const indexA = SERVICE_ORDER.indexOf(a);
    const indexB = SERVICE_ORDER.indexOf(b);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  // Transform services to steps array
  const steps = serviceNames.map((serviceName, index) => {
    const serviceData = services[serviceName] || {};
    const progress = serviceData.progress || 0;
    const status = serviceData.status || (progress >= 100 ? 'completed' : progress > 0 ? 'running' : 'pending');
    
    return {
      name: serviceName,
      display_name: SERVICE_DISPLAY_NAMES[serviceName] || serviceName,
      status: status,
      progress: Math.min(100, Math.max(0, progress)),
      message: serviceData.message || '',
      started_at: serviceData.timestamp ? new Date(serviceData.timestamp * 1000) : null,
      completed_at: serviceData.completed_at ? new Date(serviceData.completed_at) : (progress >= 100 && serviceData.timestamp ? new Date(serviceData.timestamp * 1000) : null),
      duration: serviceData.duration || null,
      description: getServiceDescription(serviceName),
      instance_id: serviceData.instance_id || null,
      folder_id: serviceData.folder_id || null,
      folder_name: serviceData.folder_name || null
    };
  });

  // Determine overall status
  let overallStatus = 'pending';
  if (completed_services === total_services && total_services > 0) {
    overallStatus = 'completed';
  } else if (overall_progress > 0) {
    overallStatus = 'running';
  }

  // Calculate completed steps count
  const completedSteps = steps.filter(step => step.status === 'completed').length;

  return {
    id: taskMetadata.run_key || taskMetadata.run_id || taskMetadata.task_id || 'unknown',
    project_id: taskMetadata.project_id || null,
    project_name: taskMetadata.project_name || `Project ${taskMetadata.project_id || 'Unknown'}`,
    run_id: taskMetadata.run_key || taskMetadata.run_id || taskMetadata.task_id || null,
    status: overallStatus,
    start_time: taskMetadata.created_at ? new Date(taskMetadata.created_at) : new Date(),
    completed_at: overallStatus === 'completed' ? new Date() : null,
    progress: {
      percentage: Math.round(overall_progress),
      completed_steps: completedSteps,
      total_steps: steps.length || total_services
    },
    steps: steps,
    input_files: taskMetadata.input_files || [],
    user_id: taskMetadata.user_email || null,
    // Keep original backend data for reference
    _raw: backendData
  };
};

/**
 * Get service description
 * @param {string} serviceName - Service name
 * @returns {string} Description
 */
const getServiceDescription = (serviceName) => {
  const descriptions = {
    'tiling': 'Breaking large LiDAR files into manageable tiles for processing',
    'csf': 'Classifying ground points using CSF algorithm',
    'road_detection': 'Detecting road boundaries',
    'car_removal': 'Removing vehicles and other moving objects from the point cloud',
    'file_transfer': 'Transferring chunked LAS/LAZ files to the per-analysis prefixes',
    'create_rsa': 'Running RSA core analysis (sight distance, etc.)',
    'ssd_classification': 'Stopping Sight Distance ML classification',
    'psd_classification': 'Passing Sight Distance ML classification',
    'shapefile': 'Generating GIS shapefile outputs from analysis results',
    'horizontal': 'Calculating horizontal curves and alignments',
    'slope_calc': 'Calculating slope values and gradients',
    'embankment': 'Analyzing road embankments',
    'clear_zone': 'Analyzing roadside clear zones',
    'guardrail': 'Analyzing guardrails',
    'automatic_reports': 'Generating automatic analysis reports',
    'automatic_report': 'Generating automatic analysis reports',
    'report_generation': 'Generating final analysis report with visualizations'
  };
  return descriptions[serviceName] || `Processing ${serviceName}`;
};

/**
 * Transform task list item for projects-list
 * @param {Object} task - Task object from backend
 * @returns {Object} Transformed task for list display
 */
export const transformTaskForList = (task) => {
  const {
    run_id,
    run_key,
    full_run_id,
    task_id,
    project_id,
    source_project_id,
    mapped_project_id,
    project_type,
    project_name,  // Add project_name
    user_email,
    folder_id,
    services = [],
    services_progress = {},
    overall_progress = 0,
    status = 'pending',
    created_at,
    updated_at,
    source,
    metadata = {}
  } = task;

  const metadataLaunchPayload = metadata && typeof metadata.launch_payload === 'object'
    ? metadata.launch_payload
    : {};
  const resolvedSourceProjectId = source_project_id
    ?? metadata?.source_project_id
    ?? metadataLaunchPayload?.source_project_id
    ?? null;
  const resolvedMappedProjectId = mapped_project_id
    ?? project_id
    ?? metadata?.project_id
    ?? metadataLaunchPayload?.project_id
    ?? null;
  const effectiveProjectId = resolvedSourceProjectId ?? resolvedMappedProjectId;

  // Count services by status
  const serviceEntries = Object.entries(services_progress);
  const completedServices = serviceEntries.filter(([_, data]) => 
    (data.progress || 0) >= 100 || data.status === 'completed'
  ).length;
  const runningServices = serviceEntries.filter(([_, data]) => 
    (data.progress || 0) > 0 && (data.progress || 0) < 100 && data.status === 'processing'
  ).length;

  // Generate a more descriptive name
  const canonicalRunId = run_key || full_run_id || task_id || run_id;
  const displayRunId = run_id || canonicalRunId;
  const runDisplayName = effectiveProjectId
    ? `Project ${effectiveProjectId} - Run ${displayRunId || 'Unknown'}`
    : `Run ${displayRunId || 'Unknown'}`;
  
  return {
    id: canonicalRunId,
    name: project_name || runDisplayName,  // Use project_name if available, otherwise use generated name
    description: effectiveProjectId
      ? `Pipeline run for project ${effectiveProjectId}`
      : `Pipeline run ${displayRunId || 'Unknown'}`,
    project_id: effectiveProjectId,
    source_project_id: resolvedSourceProjectId,
    mapped_project_id: resolvedMappedProjectId,
    project_type: project_type || metadata?.project_type || null,
    project_name: project_name,  // Include project_name in returned object
    run_id: canonicalRunId,
    run_display_id: displayRunId,
    full_run_id: full_run_id || canonicalRunId,
    total_analyses: 1, // Each run is one analysis
    completed_analyses: status === 'completed' ? 1 : 0,
    running_analyses: status === 'processing' || status === 'running' ? 1 : 0,
    last_updated: updated_at ? new Date(updated_at) : (created_at ? new Date(created_at) : new Date()),
    overall_progress: overall_progress,
    status: status,
    services: services,
    services_progress: services_progress,
    created_at: created_at,
    user_email: user_email,
    folder_id: folder_id,
    metadata: metadata,
    source: source // 'redis' or 'mongodb'
  };
};

